import { useState } from "react";

import { casePageUrl, type DocumentOut } from "@/api";

import { Button } from "./Button";
import { Dialog, DialogFooter } from "./Dialog";
import styles from "./SketchViewer.module.css";

/** How many pages a drawing has, as far as anybody knows — a picture has one, an unknown count is one. */
export function sketchPages(document: Pick<DocumentOut, "pageCount">): number {
  return Math.max(1, document.pageCount ?? 1);
}

/** „Skizze, Seite 2 von 3“ — the page's name, wherever it is drawn. */
export function sketchPageName(page: number, pages: number): string {
  return `Skizze, Seite ${page} von ${pages}`;
}

interface SketchViewerProps {
  caseId: string;
  document: DocumentOut;
  initialPage?: number;
  onClose: () => void;
}

/**
 * A customer's drawing large, in the app (Task 18; all three UX reviews):
 * „Ansehen“ used to open the raw image in the tab and leave the Werkbank,
 * and the planning had nothing to compare its plan with. One dialog for the
 * record, the Eingang and the planning — the stored page as the server draws
 * it (`casePageUrl`, same origin, nothing from elsewhere), page by page where
 * there are several. Only to look at: nothing is read from it here (E-54).
 */
export function SketchViewer({ caseId, document, initialPage = 1, onClose }: SketchViewerProps) {
  const pages = sketchPages(document);
  const startPage = Math.min(Math.max(1, initialPage), pages);
  const [page, setPage] = useState(startPage);
  const [failed, setFailed] = useState<number | null>(null);
  const where = `Seite ${page} von ${pages}`;

  return (
    <Dialog title={document.originalFilename} subtitle={pages > 1 ? where : undefined} size="wide" onClose={onClose}>
      {/* The page is a sheet (design 2026-10-24 §5.3): paper in the dark, the
          dialog around it in the mode — the page itself, on the drawings' one
          paper tone, and round it the desk's well, never a white frame
          (UX-Gutachten 2, B-10). */}
      <figure className={styles.stage}>
        {failed === page ? (
          <p className={styles.failed}>Diese Seite lässt sich nicht zeigen — die Datei liegt in der Akte.</p>
        ) : (
          <img
            key={page}
            className={styles.page}
            src={casePageUrl(caseId, document.id, page)}
            alt={sketchPageName(page, pages)}
            data-blatt="zeichnung"
            onError={() => setFailed(page)}
          />
        )}
      </figure>
      {/* A turned page is said, not only shown (review T18-3). */}
      <p role="status" className="visually-hidden">
        {pages > 1 ? where : ""}
      </p>
      <DialogFooter>
        {pages > 1 ? (
          <>
            {/* The focus starts on the way that leads somewhere (review
                T18-3): on, where there is a next page; back from the last. */}
            <Button
              aria-disabled={page <= 1 ? "true" : undefined}
              data-autofocus={startPage >= pages ? "" : undefined}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Vorige Seite
            </Button>
            <Button
              aria-disabled={page >= pages ? "true" : undefined}
              data-autofocus={startPage < pages ? "" : undefined}
              onClick={() => setPage((current) => Math.min(pages, current + 1))}
            >
              Nächste Seite
            </Button>
          </>
        ) : null}
        <Button variant="primary" data-autofocus={pages > 1 ? undefined : ""} onClick={onClose}>
          Schließen
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
